import { FocusEvent, useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/i18n';
import { useFormContext } from 'react-hook-form';
import { CheckoutApi } from '../api/checkoutApi';
import { CheckoutFormValues, CustomerState, EMAIL_PATTERN, Market, PHONE_PREFIXES, joinPhonePrefix, stripPhonePrefix } from '../types';
import Field from './Field';
import { Collapse } from './ui';

/** Nejkratší reálné číslo i s předvolbou — kratší vstup („+420", „0") nemá cenu posílat. */
const MIN_PHONE_DIGITS = 7;

interface Props {
    api: CheckoutApi;
    customer: CustomerState | null;
    market: Market;
}

/** Kontaktní údaje: jméno, příjmení, e-mail (kontrola existence), telefon (libphonenumber přes API). */
export default function PersonSection({ api, customer, market }: Props) {
    const t = useI18n();
    const { register, setError, clearErrors, watch, getValues, setValue, formState: { errors } } = useFormContext<CheckoutFormValues>();
    const [emailExists, setEmailExists] = useState(false);
    const [emailStatus, setEmailStatus] = useState<'loading' | 'valid' | null>(null);
    const [phoneStatus, setPhoneStatus] = useState<'loading' | 'valid' | null>(null);
    const wantContactEmail = watch('person.wantContactEmail');
    const createAccount = watch('agreements.createAccount');
    const emailServerError = errors.person?.email?.type === 'server';
    const phonePrefix = PHONE_PREFIXES[market];
    const phoneInvalidMsg = t('You need a valid phone number (including country calling code)');
    // výsledky ověření čísla (libphonenumber přes API) podle čísla s předvolbou — chyba z API
    // musí přežít revalidaci při odeslání (handleSubmit by setError smazal)
    const phoneVerdicts = useRef(new Map<string, boolean>());

    const checkEmail = async (email: string) => {
        if (customer || email === '') {
            return;
        }
        setEmailStatus('loading');
        try {
            const result = await api.emailCheck(email);
            setEmailExists(result.exists);
            setEmailStatus(result.validEmail && !result.exists ? 'valid' : null);
        } catch {
            // nekritické — server e-mail zvaliduje při submitu
            setEmailStatus(null);
        }
    };

    // Server odmítne registraci na existující e-mail (422 person.email). E-mail mohl vyplnit
    // autofill bez blur nebo kontrola selhala — ověříme znovu, ať se ukáže nabídka níže.
    useEffect(() => {
        if (emailServerError && createAccount && !emailExists) {
            void checkEmail(getValues('person.email'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailServerError]);

    /** Nákup jako host: registrace na existující e-mail nejde, objednávka bez účtu ano. */
    const orderWithoutAccount = () => {
        setValue('agreements.createAccount', false);
        setValue('agreements.registration', false);
        clearErrors(['person.email', 'agreements.registration']);
    };

    const checkPhone = async (phone: string) => {
        if (phone === '') {
            setPhoneStatus(null);
            return;
        }
        setPhoneStatus('loading');
        try {
            const joined = joinPhonePrefix(market, phone);
            const result = await api.phoneValidate(joined);
            phoneVerdicts.current.set(joined, result.valid);
            if (!result.valid) {
                setPhoneStatus(null);
                setError('person.phone', { type: 'remote', message: phoneInvalidMsg });
            } else {
                if (result.normalized) {
                    phoneVerdicts.current.set(result.normalized, true);
                }
                // zakaznik hned vidi cislo s predvolbou (E.164); guard proti prepsani, kdyz mezitim dal psal
                if (result.normalized && getValues('person.phone') === phone) {
                    setValue('person.phone', stripPhonePrefix(market, result.normalized));
                }
                clearErrors('person.phone');
                setPhoneStatus('valid');
            }
        } catch {
            // nekritické
            setPhoneStatus(null);
        }
    };

    const phoneRules = {
        required: phoneInvalidMsg,
        validate: (value: string) => {
            const joined = joinPhonePrefix(market, value);
            if (joined.replace(/\D/g, '').length < MIN_PHONE_DIGITS || phoneVerdicts.current.get(joined) === false) {
                return phoneInvalidMsg;
            }
            // neověřené číslo pustit — server ho zkontroluje při odeslání
            return true;
        },
        onBlur: (e: FocusEvent<HTMLInputElement>) => void checkPhone(e.target.value),
    };

    return (
        <fieldset>
            <div className="b-person clearfix">
                <p className="basket__title mt-6">{t('Kontaktní údaje')}</p>

                <Field name="person.name" label={t('Jméno')} required>
                    <input
                        id="frm-person-name"
                        className="form-control"
                        autoComplete="given-name"
                        {...register('person.name', { required: t('Zadejte prosím jméno') })}
                    />
                </Field>

                <Field name="person.surname" label={t('Příjmení')} required>
                    <input
                        id="frm-person-surname"
                        className="form-control"
                        autoComplete="family-name"
                        {...register('person.surname', { required: t('Zadejte prosím příjmení') })}
                    />
                </Field>

                {emailExists && (
                    <p className="alert alert-warning no-margin--top mb-6">
                        {createAccount
                            ? t('Účet s tímto e-mailem už u nás existuje. Přihlaste se, nebo objednejte bez vytvoření účtu.')
                            : t('Tento účet je již u nás registrován, přihlaste se prosím.')}{' '}
                        <button type="button" className="btn btn--custom-1 ml-6 btn-sm" data-toggle="modal" data-target="#fn_login-dialog">
                            {t('Přihlásit se')}
                        </button>
                        {createAccount && (
                            <button type="button" className="btn btn--custom-1 ml-6 btn-sm" onClick={orderWithoutAccount}>
                                {t('Objednat bez vytvoření účtu')}
                            </button>
                        )}
                    </p>
                )}

                <Field
                    name="person.email"
                    label={t('E-mail')}
                    required
                    status={emailStatus}
                    help={customer ? <p><strong>{t('E-mail je svázán s Vaším účtem')}</strong></p> : undefined}
                >
                    <input
                        id="frm-person-email"
                        type="email"
                        className="form-control"
                        autoComplete="email"
                        readOnly={!!customer}
                        {...register('person.email', {
                            required: t('Zadejte prosím platnou e-mailovou adresu.'),
                            pattern: { value: EMAIL_PATTERN, message: t('Zadejte prosím platnou e-mailovou adresu.') },
                            onBlur: (e) => void checkEmail(e.target.value),
                        })}
                    />
                </Field>

                {customer?.showContactEmail && (
                    <>
                        <div className="clearfix">
                            <div className="form-group col-xm-6 col-md-5 no-padding--left">
                                <label className={`b-check b-check--small b-check--large--no-underline b-check--block${wantContactEmail ? ' active-panel' : ''}`}>
                                    <span>{t('Jiný kontaktní email')}</span>
                                    <input type="checkbox" {...register('person.wantContactEmail')} />
                                </label>
                            </div>
                        </div>
                        <Collapse open={wantContactEmail}>
                            <Field
                                name="person.contactEmail"
                                label={t('Kontaktní E-mail')}
                                required
                                help={<p><strong>{t('Na tento email Vám budou zasílány kopie o stavu objednávky')}</strong></p>}
                            >
                                <input
                                    id="frm-person-contactEmail"
                                    type="email"
                                    className="form-control"
                                    {...register('person.contactEmail', {
                                        validate: (value, values) =>
                                            !values.person.wantContactEmail || EMAIL_PATTERN.test(value) || t('Zadejte prosím platnou e-mailovou adresu.'),
                                    })}
                                />
                            </Field>
                        </Collapse>
                    </>
                )}

                <Field name="person.phone" label={t('Telefon')} required status={phoneStatus}>
                    {phonePrefix ? (
                        // CZ/SK: pevná předvolba jako na starých webech — layout převzatý z legacy šablony
                        <div className="row">
                            <div className="col-xs-4 col-sm-5 col-md-4">
                                <input className="form-control" value={phonePrefix} readOnly tabIndex={-1} aria-label={t('Předvolba')} />
                            </div>
                            <div className="col-xs-8 col-sm-7 col-md-8 no-padding--left">
                                <input
                                    id="frm-person-phone"
                                    className="form-control"
                                    autoComplete="tel-national"
                                    {...register('person.phone', phoneRules)}
                                />
                            </div>
                        </div>
                    ) : (
                        <input
                            id="frm-person-phone"
                            className="form-control"
                            autoComplete="tel"
                            {...register('person.phone', phoneRules)}
                        />
                    )}
                </Field>
            </div>
        </fieldset>
    );
}
